import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { LogoutButton } from '../components/LogoutButton';
import { NotificationsBell } from '../components/NotificationsBell';
import './TransferSuccess.css';
import {
  Check, AlertCircle, Zap, ArrowLeft, ArrowRight,
  User, FileText, Calendar, Hash, ArrowUpRight, ArrowDownLeft, Receipt,
} from 'lucide-react';

const APP_NAME = import.meta.env.VITE_APP_NAME || 'Voltary';

interface TransferData {
  id: number;
  fromUserId: string;
  toUserId: string;
  fromUsername?: string;
  toUsername?: string;
  amount: number;
  concept: string;
  createdAt: string;
  type: 'sent' | 'received';
}

export function TransferSuccess() {
  const [searchParams] = useSearchParams();
  const transferId = searchParams.get('id');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [transfer, setTransfer] = useState<TransferData | null>(null);

  useEffect(() => {
    if (transferId) {
      fetchTransferData();
    } else {
      setStatus('error');
    }
  }, [transferId]);

  const fetchTransferData = async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/transfer/${transferId}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setTransfer(data.transfer);
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch (err) {
      console.error('Error fetching transfer:', err);
      setStatus('error');
    }
  };

  if (status === 'loading') {
    return (
      <div className="db-page">
        <header className="sp-topbar">
          <div className="sp-topbar-left">
            <Link to="/dashboard" className="sp-topbar-brand">
              <Zap size={16} />
              <span>{APP_NAME}</span>
            </Link>
            <span className="sp-topbar-divider" aria-hidden="true" />
            <span className="sp-topbar-crumb">Comprobante</span>
          </div>
          <div className="sp-topbar-right">
            <NotificationsBell />
            <LogoutButton />
          </div>
        </header>
        <main className="db-main">
          <div className="db-loading">
            <div className="spinner" />
            <p>Cargando transferencia…</p>
          </div>
        </main>
      </div>
    );
  }

  if (status === 'error' || !transfer) {
    return (
      <div className="db-page">
        <header className="sp-topbar">
          <div className="sp-topbar-left">
            <Link to="/dashboard" className="sp-topbar-brand">
              <Zap size={16} />
              <span>{APP_NAME}</span>
            </Link>
            <span className="sp-topbar-divider" aria-hidden="true" />
            <span className="sp-topbar-crumb">Comprobante</span>
          </div>
          <div className="sp-topbar-right">
            <NotificationsBell />
            <LogoutButton />
          </div>
        </header>
        <main className="db-main">
          <section className="ts-error">
            <div className="ts-error-icon">
              <AlertCircle size={32} />
            </div>
            <h1>No se pudo cargar la transferencia</h1>
            <p>No encontramos la información de esta transferencia. Puede que el enlace haya expirado o que la transferencia no exista.</p>
            <Link to="/dashboard" className="btn btn-primary">
              <ArrowLeft size={14} /> Volver al Dashboard
            </Link>
          </section>
        </main>
      </div>
    );
  }

  const isSent = transfer.type === 'sent';
  const counterpartyUser = isSent ? transfer.toUserId : transfer.fromUserId;
  const counterpartyUsername = isSent ? transfer.toUsername : transfer.fromUsername;
  const counterpartyLabel = isSent ? 'Enviado a' : 'Recibido de';

  return (
    <div className="db-page">
      {/* TOPBAR */}
      <header className="sp-topbar">
        <div className="sp-topbar-left">
          <Link to="/dashboard" className="sp-topbar-brand">
            <Zap size={16} />
            <span>{APP_NAME}</span>
          </Link>
          <span className="sp-topbar-divider" aria-hidden="true" />
          <span className="sp-topbar-crumb">Comprobante de transferencia</span>
        </div>
        <div className="sp-topbar-right">
          <NotificationsBell />
          <LogoutButton />
        </div>
      </header>

      <main className="db-main">
        {/* HERO con checkmark + monto grande */}
        <section className={`ts-hero ${isSent ? 'ts-hero--sent' : 'ts-hero--received'}`}>
          <div className="ts-hero-checkmark">
            <div className="ts-checkmark-circle">
              <Check size={48} strokeWidth={3} />
            </div>
            <div className="ts-checkmark-pulse" aria-hidden="true" />
          </div>

          <h1 className="ts-hero-title">Transferencia completada</h1>
          <p className="ts-hero-sub">
            {isSent
              ? 'Has enviado saldo exitosamente'
              : 'Has recibido saldo en tu cuenta'}
          </p>

          <div className="ts-hero-amount">
            <span className="ts-hero-amount-currency">$</span>
            {transfer.amount.toFixed(2)}
            <span className="ts-hero-amount-suffix">USD</span>
          </div>

          <p className="ts-hero-note">
            {isSent
              ? 'El saldo ha sido debitado de tu cuenta.'
              : 'El saldo ha sido acreditado a tu cuenta.'}
          </p>
        </section>

        {/* PANEL DE DETALLES */}
        <div className="sp-grid">
          <section className="sp-panel sp-col-8 sp-col-sm-12">
            <div className="sp-panel-header">
              <Receipt className="sp-panel-icon" />
              <h2 className="sp-panel-title">Detalles de la operación</h2>
            </div>
            <div className="sp-panel-body">
              <div className="sp-info-list">
                <div className="sp-info-row">
                  <span className="sp-info-row-label">Tipo</span>
                  <span className="sp-info-row-value">
                    <span className={`ts-type-pill ${isSent ? 'ts-type-pill--sent' : 'ts-type-pill--received'}`}>
                      {isSent ? <ArrowUpRight size={12} /> : <ArrowDownLeft size={12} />}
                      {isSent ? 'Envío' : 'Recepción'}
                    </span>
                  </span>
                </div>

                <div className="sp-info-row">
                  <span className="sp-info-row-label">
                    <User size={12} /> {counterpartyLabel}
                  </span>
                  <span className="sp-info-row-value">
                    {counterpartyUsername ? (
                      <>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                          {counterpartyUsername}
                        </span>
                        <code style={{ marginLeft: '0.5rem', fontSize: '0.78rem' }}>
                          {counterpartyUser}
                        </code>
                      </>
                    ) : (
                      <code>{counterpartyUser}</code>
                    )}
                  </span>
                </div>

                <div className="sp-info-row">
                  <span className="sp-info-row-label">
                    <FileText size={12} /> Concepto
                  </span>
                  <span className="sp-info-row-value">
                    {transfer.concept}
                  </span>
                </div>

                <div className="sp-info-row">
                  <span className="sp-info-row-label">
                    <Calendar size={12} /> Fecha
                  </span>
                  <span className="sp-info-row-value">
                    {new Date(transfer.createdAt).toLocaleString('es-ES', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>

                <div className="sp-info-row">
                  <span className="sp-info-row-label">
                    <Hash size={12} /> ID de transacción
                  </span>
                  <span className="sp-info-row-value">
                    <code className="ts-id-code">#{transfer.id}</code>
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* SIDEBAR — acciones rápidas */}
          <section className="sp-panel sp-col-4 sp-col-sm-12">
            <div className="sp-panel-header">
              <ArrowRight className="sp-panel-icon" />
              <h2 className="sp-panel-title">¿Qué sigue?</h2>
            </div>
            <div className="sp-panel-body">
              <div className="ts-actions">
                <Link to="/dashboard" className="btn btn-primary ts-action-primary">
                  <ArrowLeft size={14} /> Volver al Dashboard
                </Link>
                <Link to="/transfer" className="btn btn-ghost">
                  <ArrowUpRight size={14} /> Nueva transferencia
                </Link>
                <Link to="/buy-credits" className="btn btn-ghost">
                  <Zap size={14} /> Recargar saldo
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
